import { Annotations } from '@/components/annotations/annotations';
import { Card, Flex, message } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import styles from './conversation.module.scss';
import { TranscriptMessage } from '@/components/transcript-message/transcript-message';
import { StartEndCall } from '@/components/start-end-call/start-end-call';
import CallRecording from '@/components/call-recording/call-recording';
import { convertToSeconds, downloadAudioFile } from '@/utils/helpers';
import { useGetTxtFile } from '@/hooks/use-get-txt-file/use-get-txt-file';
import { useAnalyticsCall } from '@/hooks/use-conversations/use-conversations';
import LanguagesSwitch from '@/components/language-switch/language-switch';
import Loader from '@/components/loader/loader';
import { SurveyCard } from '@/components/survey-card/survey-card';
import { MaintenanceCard } from '@/components/maintenance-card/maintenance-card';
import { ConversationHeader } from '@/components/conversation-header/conversation-header';
import { useReviewedCall } from '@/hooks/use-reviewed-call/use-reviewed-call';
import { Checkbox } from '@/components/checkbox/checkbox';

export function Conversation() {
  const [annotation, setAnnotation] = useState('');
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:00');
  const [isAudioPending, setIsAudioPending] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);

  const [translate, setTranslate] = useState('false');

  const { callId = '' } = useParams();

  const [messageApi, contextHolder] = message.useMessage();

  const { analyticsCall, addAnnotation, isAddingAnnotation, analyticsCallIsPending } =
    useAnalyticsCall({
      analytics_call_id: callId,
      translate: translate,
    });

  const { downloadTXT, isPending } = useGetTxtFile(+callId);

  const { checkCall } = useReviewedCall({ analytics_call_id: callId });

  const handleLanguageSwitch = (isTranslate: string) => {
    setTranslate(isTranslate);
  };

  const handleDownloadTxt = () => {
    downloadTXT().then(() => {
      messageApi.open({
        type: 'success',
        content: 'File downloaded successfully',
      });
    });
  };

  const isAgentCall = analyticsCall?.agent;
  const recordingUrl = analyticsCall?.audio_url || analyticsCall?.agent_audio_url;
  const fullURL = window.location.href;

  const handleShare = () => {
    navigator.clipboard.writeText(fullURL).then(() => {
      messageApi.open({
        type: 'success',
        content: 'Link has been copied',
      });
    });
  };

  const handleDownloadAudio = () => {
    if (recordingUrl) {
      setIsAudioPending(true);
      fetch(recordingUrl)
        .then((response) => response.blob())
        .then((audioBlob) => {
          downloadAudioFile(audioBlob, 'call-audio');
          messageApi.open({
            type: 'success',
            content: 'Audio file downloaded successfully',
          });
        })
        .catch(() => {
          messageApi.open({
            type: 'error',
            content: 'Failed to download the audio file',
          });
        })
        .finally(() => {
          setIsAudioPending(false);
        });
    }
  };

  const language = analyticsCall?.language;
  const isEnglish = language?.toLowerCase().startsWith('en');
  const hideToggleButton = language !== null && !isEnglish;

  useEffect(() => {
    if (analyticsCall?.reviewed !== undefined) {
      setIsReviewed(analyticsCall.reviewed);
    }
  }, [analyticsCall?.reviewed]);

  return (
    <Flex vertical>
      {contextHolder}
      {!analyticsCall ? (
        <Loader />
      ) : (
        <>
          <Card
            styles={{ header: { padding: 15 } }}
            title={
              <ConversationHeader
                phone={analyticsCall?.phone || ''}
                id={analyticsCall?.id || 0}
                topic={analyticsCall?.topics || []}
                summary={analyticsCall?.convo_summary || ''}
                detectedTags={analyticsCall?.topics || []}
              />
            }
            className={styles.cardHeader}
          >
            {recordingUrl ? (
              <CallRecording
                isDownloadingAudio={isAudioPending}
                handleDownloadAudio={handleDownloadAudio}
                handleShare={handleShare}
                handleDownloadAsTxt={handleDownloadTxt}
                isDownloadingTxt={isPending}
                currentTime={currentTime}
                setCurrentTime={setCurrentTime}
                duration={duration}
                setDuration={setDuration}
                status={analyticsCall?.status}
                connectAgent={analyticsCall?.agent}
                src={recordingUrl}
                isLoading={analyticsCallIsPending}
                callDate={new Date(analyticsCall?.created_at || 0)}
                callDuration={analyticsCall?.duration || 0}
                smsScore={analyticsCall?.sms_score}
                smsSummary={analyticsCall?.sms_body || ''}
                smsReceived={analyticsCall?.sms_received}
                maintenances={analyticsCall?.maintenance}
                relatedCall={analyticsCall?.related_call}
                callType={analyticsCall?.type}
              />
            ) : (
              <Flex vertical>
                <Flex justify="center" style={{ padding: 16 }}>
                  Call recording not available
                </Flex>
                <Flex className={styles.widgetWrapper}>
                  {!recordingUrl && (
                    <SurveyCard
                      className={styles.surveyCard}
                      score={analyticsCall?.sms_score}
                      smsSummary={analyticsCall?.sms_body}
                      smsReceived={analyticsCall?.sms_received}
                    />
                  )}
                  {analyticsCall?.maintenance && analyticsCall?.maintenance.length > 0 && (
                    <MaintenanceCard
                      className={styles.maintenanceCard}
                      maintenances={analyticsCall.maintenance}
                      carouselClassName={styles.carousel}
                    />
                  )}
                </Flex>
              </Flex>
            )}
          </Card>
          <Flex gap={10}>
            <p>Mark call as reviewed</p>
            <Checkbox
              className={styles.statusCheckbox}
              checked={isReviewed}
              onChange={(e) => {
                const value = e.target.checked;
                setIsReviewed(value);
                checkCall(value);
              }}
            />
          </Flex>

          <Flex>
            <p className={styles.transcriptTitle}>Transcript</p>
            <p className={styles.annotationsTitle}>Segment Annotations</p>
          </Flex>
          <Card styles={{ body: { paddingLeft: 30, paddingRight: 30 } }}>
            <Flex justify="space-between" className={styles.container}>
              <Flex vertical className={styles.transcriptContainer}>
                {analyticsCallIsPending ? (
                  <Flex vertical align="center" className={styles.recordingFallback}>
                    <Loader />
                    <p>Translation is loading</p>
                  </Flex>
                ) : (
                  <>
                    {hideToggleButton && (
                      <div className={styles.languageSwitch}>
                        <LanguagesSwitch
                          initialActive={translate === 'false' ? 'original' : 'english'}
                          onSwitch={handleLanguageSwitch}
                          flagUrl={analyticsCall.language_flag_url || ''}
                        />
                      </div>
                    )}
                    <StartEndCall type="start" phone={analyticsCall?.phone || ''} />
                    {analyticsCall?.transcriptions.map(
                      ({ id, answer, question, created_at, keywords, document_id }) => (
                        <div key={id}>
                          {isAgentCall && question && (
                            <TranscriptMessage
                              isAgentCall={isAgentCall}
                              type="answer"
                              messageText={question}
                              date={created_at}
                              detectedKeywords={keywords}
                              document_id={document_id}
                            />
                          )}
                          {isAgentCall && answer && (
                            <TranscriptMessage
                              isAgentCall={isAgentCall}
                              type="question"
                              messageText={answer}
                              date={created_at}
                            />
                          )}

                          {!isAgentCall && question && (
                            <TranscriptMessage
                              isAgentCall={isAgentCall}
                              type="question"
                              messageText={question}
                              date={created_at}
                            />
                          )}
                          {!isAgentCall && answer && (
                            <TranscriptMessage
                              isAgentCall={isAgentCall}
                              type="answer"
                              messageText={answer}
                              date={created_at}
                              detectedKeywords={keywords}
                              document_id={document_id}
                            />
                          )}
                        </div>
                      )
                    )}
                    <StartEndCall type="end" phone={analyticsCall?.phone || ''} />
                  </>
                )}
              </Flex>

              <hr className={styles.separator} />
              <Flex className={styles.annotationsContainer} justify="center">
                <Annotations
                  annotations={analyticsCall?.annotations || []}
                  onTextChange={setAnnotation}
                  value={annotation}
                  addAnnotation={(value) => {
                    addAnnotation({
                      message: value,
                      start_second: 0,
                      end_second: convertToSeconds(currentTime) || convertToSeconds(duration),
                    });
                    setAnnotation('');
                  }}
                  isLoading={isAddingAnnotation}
                />
              </Flex>
            </Flex>
          </Card>

          {analyticsCall.sms_summary_message && (
            <Flex vertical className={styles.smsSummaryContainer}>
              <p className={styles.smsSummaryTitle}>Sms summary message</p>
              <Card styles={{ body: { padding: 30 } }}>
                <Flex vertical gap={10}>
                  <p>{analyticsCall.sms_summary_message}</p>
                </Flex>
              </Card>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}
